import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import {
  BarChart3,
  Building2,
  Calculator,
  CalendarDays,
  CandlestickChart,
  ChevronDown,
  ClipboardList,
  FileSearch,
  FileText,
  Info,
  Landmark,
  LandPlot,
  LineChart,
  Map,
  Megaphone,
  MessageSquare,
  Notebook,
  Star,
  StarHalf,
  Target,
  TrendingUpDown,
  Trophy,
  User,
  Wallet,
} from 'lucide-react';

import { journalRoutePaths } from '@/app/routes/journal';
import { Button, cn } from '@/shared';
import { t } from '@/i18n';
import { computeIsActive } from '@/shared/lib/utils/nav-active-path';

/**
 * TRAD-3069 — 좌측 사이드바 → 상단 내비.
 * 정본 시안: docs-ops/2026-09-02-TRAD-3050-web-renewal.html §6 (특히 §6-2/§6-2a/§6-7).
 *
 * 그룹 구조는 사이드바(AppSidebar.tsx)의 그대로 재사용이 아니라 §6-2 배분표를 따른다 —
 * 순서(시세·부동산·예측시장·금융·뉴스·커뮤니티)가 사이드바 순서(예측시장이 부동산보다 앞)와
 * 다르고, 파생이 더 이상 최상위 단독 항목이 아니라 "시세" 그룹 안으로 들어간다(§6-5).
 * 🔴 파생을 다시 최상위로 올릴지는 davidyoon 미결정 사항이다(조건3, Jira 수용기준 B-3) —
 * 임의로 결정하지 않고 지금은 시안대로 "시세" 안에 둔다.
 */

interface TopNavItem {
  label: string;
  path: string;
  icon: React.ElementType;
}

interface TopNavSubLabel {
  subLabel: string;
}

interface TopNavSubSeparator {
  subSeparator: true;
}

type TopNavPanelEntry = TopNavItem | TopNavSubLabel | TopNavSubSeparator;

interface TopNavGroup {
  /** 그룹 라벨. 드롭다운 트리거에 그대로 노출된다. */
  label: string;
  /**
   * 랜딩 라우트. 있으면 트리거가 <Link>(클릭 시 이동 + 호버/포커스 시 패널 오픈),
   * 없으면 <button>(메뉴 전용, 자체 목적지 없음 — 사이드바의 group.path 관례와 동일:
   * AppSidebar.tsx 129~141줄 "금융" 그룹 참고).
   */
  path?: string;
  items: TopNavPanelEntry[];
}

const isTopNavSubLabel = (e: TopNavPanelEntry): e is TopNavSubLabel => 'subLabel' in e;
const isTopNavSubSeparator = (e: TopNavPanelEntry): e is TopNavSubSeparator => 'subSeparator' in e;

// TRAD-2057/TRAD-2490과 동일 이유로 함수 안에서 t()를 호출한다(모듈 스코프 상수 금지 —
// module-scope-t-call-guard.test.ts가 이걸 전수 스캔으로 고정한다).
export const buildTopNavGroups = (): TopNavGroup[] => [
  {
    label: t('nav.crypto.label'),
    path: '/market',
    items: [
      { label: t('nav.crypto.market'), path: '/market', icon: BarChart3 },
      { label: t('nav.derivatives'), path: '/derivatives', icon: CandlestickChart },
      { label: t('nav.crypto.kimchi_premium'), path: '/kimchi-premium', icon: TrendingUpDown },
      { label: t('nav.crypto.ai_report'), path: '/crypto-report', icon: FileText },
      // TRAD-3796(davidyoon 2026-10-22 «코인 평가는 암호화폐 메뉴로 옮겨») — 커뮤니티 그룹에서 이리로 옮겼다.
      // 배경·라벨 키·아이콘 선택 이유는 아래 buildCommunityGroup 의 TRAD-3192 주석(정정 포함) 참고.
      { label: t('evaluation.meta.title'), path: '/evaluation', icon: StarHalf },
    ],
  },
  {
    label: t('nav.real_estate.label'),
    path: '/real-estate',
    items: [
      { subLabel: t('nav.real_estate.section_price') },
      { label: t('nav.real_estate.map'), path: '/real-estate/map', icon: Map },
      { label: t('nav.real_estate.trades'), path: '/real-estate/trades', icon: LandPlot },
      { label: t('nav.real_estate.buildings'), path: '/real-estate/buildings', icon: FileSearch },
      { label: t('nav.real_estate.loan_calculator'), path: '/real-estate/loan', icon: Calculator },
      {
        label: t('nav.real_estate.affordability_calculator'),
        path: '/real-estate/affordability',
        icon: Calculator,
      },
      { subLabel: t('nav.real_estate.section_subscription') },
      { label: t('nav.real_estate.subscriptions'), path: '/real-estate/subscriptions', icon: ClipboardList },
      { label: t('nav.real_estate.score'), path: '/real-estate/score', icon: Target },
      { subSeparator: true },
      { label: t('nav.real_estate.watchlist'), path: '/real-estate/watchlist', icon: Star },
    ],
  },
  {
    label: t('nav.prediction.tab_label'),
    path: '/polymarket',
    items: [
      { label: t('nav.prediction.market'), path: '/polymarket', icon: BarChart3 },
      { label: t('nav.prediction.my_activity'), path: '/polymarket/my', icon: User },
      { label: t('nav.prediction.ranking'), path: '/polymarket/leaderboard', icon: Trophy },
    ],
  },
  {
    // 랜딩 라우트 없음(사이드바와 동일 사유 — AppSidebar.tsx:134~135 주석 참고).
    label: t('nav.financial.label'),
    items: [
      { label: t('nav.financial_calendar'), path: '/financial-calendar', icon: CalendarDays },
      { label: t('nav.economic_indicators'), path: '/economic-indicators', icon: LineChart },
      // TRAD-3867 — ~~금융 콘텐츠 «경제 관계를 그림으로»(10/29) 를 이 그룹 안에 뒀다~~ → 10/30 davidyoon «인사이트» 별도 섹션:
      //    이 그룹에서 빼고 아래 AppTopNav 의 낱개 링크 «인사이트»(금융 그룹 바로 다음)로 옮겼다.
      // TRAD-3582 — 미국 기관 보유(13F). EN «Notable Investors» / KO «투자 대가»(2026-10-05 davidyoon — 인물·기관을 함께 담는 이름, 이전 «기관 보유») (기획서 §5 — «Holdings»·«포트폴리오» 는 메뉴 이름에서 제외).
      { label: t('nav.institutions'), path: '/institutions', icon: Landmark },
    ],
  },
];

/**
 * 커뮤니티는 §6-2 순서상 "뉴스"(그룹이 아닌 낱개 링크) 뒤에 온다 — buildTopNavGroups()에
 * 같이 넣으면 렌더 순서가 인덱스에 암묵적으로 의존하게 되어 별도 함수로 분리했다.
 * (뉴스 자체는 그룹이 아니라 AppTopNav 안의 낱개 <Link>다.)
 */
export const buildCommunityGroup = (): TopNavGroup => ({
  label: t('nav.community.label'),
  path: '/board/idea',
  items: [
    { label: t('nav.community.crypto'), path: '/board/idea', icon: MessageSquare },
    { label: t('nav.community.real_estate'), path: '/community/real-estate', icon: Building2 },
    // TRAD-3192 — 「종목 평가」는 라우트·프리렌더(sitemap daily·0.6)는 있는데 내비엔 한 번도 없었다
    // (검색엔진엔 알리면서 사용자에겐 경로 0). ~~planner 결정(2026-09-07): 시세가 아니라 커뮤니티 —
    // 시세 그룹은 거래소에서 온 시장 데이터라 사용자 의견을 섞지 않는다.~~
    // 🔸 2026-10-22 정정(TRAD-3796, davidyoon «웹에 코인평가가 왜 커뮤니티 아래에 있지» → «암호화폐 메뉴로 옮겨»):
    //    항목은 위 buildTopNavGroups 의 «암호화폐» 그룹(AI 리포트 뒤)으로 옮겼다. 이 자리에는 없다.
    //    planner 의 «시장 데이터에 사용자 의견을 섞지 않는다»는 그룹 «시세» 기준이었고, 지금 «암호화폐» 그룹은
    //    시세·파생·김프·AI 리포트를 묶는 코인 주제 그룹이라 그 원칙과 충돌하지 않는다고 본다(재검토는 planner 몫).
    // 라벨은 새 키를 파지 않고 그 화면의 제목 키를 그대로 쓴다(같은 화면을 가리키는 두 문구가
    // 갈라지지 않게). 아이콘은 관심종목의 Star 와 겹치지 않게 StarHalf(별점). — 옮긴 뒤에도 그대로.
    // 🔴 모바일 하단탭 «더보기»에는 아직 없다 — 그쪽은 TRAD-3182(시트 전량화)가 다룬다.
  ],
});

/**
 * 우측 "내 정보" 유틸리티 드롭다운(§6-2 우측 열). 랜딩 라우트 없음 — 순수 메뉴.
 * 관심종목·내 자산·매매일지 / 마이페이지·공지사항·서비스 소개 두 덩어리로 나뉘는 것은
 * §6-3a 목업 그대로이고, 사이드바의 subSeparator 관례를 그대로 옮겼다.
 */
export const buildMyInfoGroup = (): TopNavGroup => ({
  label: t('nav.my_info'),
  items: [
    { label: t('nav.watchlist'), path: '/interest', icon: Star },
    { label: t('nav.portfolio'), path: '/portfolio', icon: Wallet },
    // TRAD-1978과 동일 사유로 매매일지를 여기 포함한다(사이드바의 동적 삽입과 대응).
    { label: t('nav.journal.label'), path: journalRoutePaths.list.fullPath, icon: Notebook },
    { subSeparator: true },
    { label: t('nav.my_page'), path: '/my-page', icon: User },
    { label: t('nav.notice'), path: '/notice', icon: Megaphone },
    { label: t('nav.about'), path: '/about', icon: Info },
  ],
});

/**
 * 도달 가능한 전체 라우트 집합 (TRAD-3069 회귀 가드용 — buildAllNavPaths, AppSidebar.tsx의
 * 동명 함수를 대체한다. 가드 명세: docs-ops/2026-09-02-TRAD-3050-nav-route-set-guard.md §1).
 * 🔴 '/'와 '/news'는 그룹에 안 담겨 있어도 실제로 도달 가능하다 — '/'는 헤더 로고 링크
 * (Header.tsx, 이 티켓 범위 밖·무변경), '/news'는 아래 AppTopNav의 낱개 링크다. 둘 다
 * 소스에서 안 보이는 게 아니라 "그룹 배열 밖에 있을 뿐"이라 여기서 명시적으로 더한다.
 */
export const buildAllNavPaths = (): string[] => {
  const groups = [...buildTopNavGroups(), buildCommunityGroup(), buildMyInfoGroup()];

  const paths = groups.flatMap((group) => {
    const childPaths = group.items
      .filter((item): item is TopNavItem => !isTopNavSubLabel(item) && !isTopNavSubSeparator(item))
      .map((item) => item.path);
    return group.path ? [group.path, ...childPaths] : childPaths;
  });

  // TRAD-3867 — '/insights' 도 '/news' 와 같은 낱개 링크(그룹 밖).
  paths.push('/', '/insights', '/news');

  return Array.from(new Set(paths));
};

const CLOSE_DELAY_MS = 120;

/**
 * 그룹 하나의 열림 상태 + 상호작용(호버·포커스·클릭·Esc·바깥 클릭)을 관리한다.
 * 디자이너 조건 2(Jira 수용기준 B-2) — 호버 전용 금지, 트리거는 a/button, aria-expanded 필수.
 */
const useTopNavDropdown = () => {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const triggerRefs = useRef<Record<string, HTMLElement | null>>({});
  // Esc로 닫을 때 트리거에 focus()를 되돌리는데, 그 포커스가 그룹 wrapper의 onFocus로
  // 버블링돼 곧바로 다시 열어버리는 걸 막는 1회성 억제 플래그(아래 handleGroupFocus).
  // 실제로 이 억제가 없으면 Esc가 "닫혔다가 포커스가 돌아오며 즉시 재오픈"돼 사실상
  // 안 닫히는 것처럼 보인다 — 사보타주 없이 첫 구현에서 실측으로 잡힌 버그다.
  const suppressFocusOpenRef = useRef<string | null>(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openNow = (label: string) => {
    clearCloseTimer();
    setOpenGroup(label);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    // 트리거→패널로 마우스가 잠깐 벗어나는 순간에 닫히는 깜빡임 방지용 최소 지연.
    closeTimerRef.current = window.setTimeout(() => setOpenGroup(null), CLOSE_DELAY_MS);
  };

  const closeNow = (refocusLabel?: string) => {
    clearCloseTimer();
    setOpenGroup(null);
    if (refocusLabel) {
      suppressFocusOpenRef.current = refocusLabel;
      triggerRefs.current[refocusLabel]?.focus();
    }
  };

  const registerTrigger = (label: string) => (el: HTMLElement | null) => {
    triggerRefs.current[label] = el;
  };

  // onFocus에서 openNow 대신 이걸 쓴다 — closeNow()가 되돌린 포커스는 열지 않는다.
  const handleGroupFocus = (label: string) => {
    if (suppressFocusOpenRef.current === label) {
      suppressFocusOpenRef.current = null;
      return;
    }
    openNow(label);
  };

  // 바깥 클릭 닫기 + Esc 닫기(포커스를 트리거로 되돌림).
  useEffect(() => {
    if (!openGroup) return undefined;

    const handlePointerDown = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        closeNow();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeNow(openGroup);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openGroup]);

  useEffect(() => clearCloseTimer, []);

  return { openGroup, navRef, registerTrigger, openNow, scheduleClose, closeNow, handleGroupFocus };
};

const panelItemClass = (active: boolean) =>
  cn(
    'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors',
    active
      ? 'bg-primary/10 text-brand-text'
      : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
  );

const TopNavPanel = ({
  group,
  align,
  isActive,
  hidden,
}: {
  group: TopNavGroup;
  align: 'left' | 'right';
  isActive: (path: string) => boolean;
  hidden: boolean;
}) => (
  <div
    role="group"
    hidden={hidden}
    className={cn(
      'absolute top-full mt-1 min-w-[13rem] rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md z-50',
      align === 'left' ? 'left-0' : 'right-0',
    )}
  >
    {group.items.map((item, idx) => {
      if (isTopNavSubSeparator(item)) {
        return <div key={`subsep-${idx}`} className="my-1 mx-2 border-t border-border/40" />;
      }
      if (isTopNavSubLabel(item)) {
        return (
          <div
            key={`sub-${item.subLabel}`}
            className={cn(
              'px-3 pt-2 pb-0.5 text-2xs font-semibold tracking-wide text-muted-foreground/60 uppercase',
              idx > 0 && 'mt-1',
            )}
          >
            {item.subLabel}
          </div>
        );
      }
      const Icon = item.icon;
      return (
        <Link key={item.path} to={item.path} className={panelItemClass(isActive(item.path))}>
          <Icon size={16} />
          {item.label}
        </Link>
      );
    })}
  </div>
);

const TopNavTrigger = ({
  group,
  isOpen,
  isActive,
  registerTrigger,
  openNow,
  closeNow,
}: {
  group: TopNavGroup;
  isOpen: boolean;
  isActive: (path: string) => boolean;
  registerTrigger: (label: string) => (el: HTMLElement | null) => void;
  openNow: (label: string) => void;
  closeNow: () => void;
}) => {
  const panelId = `top-nav-panel-${group.label}`;
  const active = group.path ? isActive(group.path) : false;
  const triggerClass = cn(
    'h-auto flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors',
    active ? 'text-brand-text' : 'text-muted-foreground hover:text-foreground hover:bg-secondary',
  );

  const chevron = (
    <ChevronDown size={14} className={cn('transition-transform', isOpen && 'rotate-180')} />
  );

  // 조건1(수용기준 B-1) — 랜딩이 있는 그룹은 트리거 자체가 <a href>다. 클릭하면 이동하고
  // 호버/포커스로 패널이 열린다. TradingView 방식 — Binance의 <span> 트리거(탭 도달 불가)를
  // 흉내내지 않는다.
  if (group.path) {
    return (
      <Link
        ref={registerTrigger(group.label)}
        to={group.path}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={triggerClass}
      >
        {group.label}
        {chevron}
      </Link>
    );
  }

  // 랜딩이 없는 그룹(금융/내 정보)은 순수 메뉴 버튼 — 클릭으로 토글, span 금지.
  return (
    <Button
      variant="ghost"
      ref={registerTrigger(group.label)}
      type="button"
      aria-haspopup="true"
      aria-expanded={isOpen}
      aria-controls={panelId}
      className={triggerClass}
      onClick={() => (isOpen ? closeNow() : openNow(group.label))}
    >
      {group.label}
      {chevron}
    </Button>
  );
};

const TopNavGroupView = ({
  group,
  align,
  isActive,
  isOpen,
  registerTrigger,
  openNow,
  scheduleClose,
  closeNow,
  handleGroupFocus,
}: {
  group: TopNavGroup;
  align: 'left' | 'right';
  isActive: (path: string) => boolean;
  isOpen: boolean;
  registerTrigger: (label: string) => (el: HTMLElement | null) => void;
  openNow: (label: string) => void;
  scheduleClose: () => void;
  closeNow: () => void;
  handleGroupFocus: (label: string) => void;
}) => (
  <div
    className="relative"
    onMouseEnter={() => openNow(group.label)}
    onMouseLeave={scheduleClose}
    onFocus={() => handleGroupFocus(group.label)}
    onBlur={(e) => {
      // 그룹(트리거+패널) 밖으로 포커스가 완전히 나갔을 때만 닫는다 — 트리거에서
      // 패널 안 항목으로 Tab 이동하는 정상적인 순서는 닫히지 않아야 한다.
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        scheduleClose();
      }
    }}
  >
    <TopNavTrigger
      group={group}
      isOpen={isOpen}
      isActive={isActive}
      registerTrigger={registerTrigger}
      openNow={openNow}
      closeNow={closeNow}
    />
    {/* TRAD-3613 — 패널을 닫혀 있어도 DOM 에 둔다. 종전에는 열릴 때만 그려서 프리렌더 HTML 에 하위 메뉴의
        <a href> 가 없었고, 메뉴 수준 페이지 절반 이상이 어디서도 링크되지 않았다(검색 봇은 hover 를 못 한다).
        닫힌 동안은 `hidden`(display:none) — 화면·탭 순서·접근성 트리에서 빠지므로 보이는 동작은 종전과 같다. */}
    <TopNavPanel group={group} align={align} isActive={isActive} hidden={!isOpen} />
  </div>
);

export const AppTopNav = ({ className }: { className?: string }) => {
  const location = useLocation();
  const { openGroup, navRef, registerTrigger, openNow, scheduleClose, closeNow, handleGroupFocus } =
    useTopNavDropdown();

  const groups = buildTopNavGroups();
  const communityGroup = buildCommunityGroup();
  const myInfoGroup = buildMyInfoGroup();
  const allPaths = buildAllNavPaths();

  const isActive = (path: string) => computeIsActive(location.pathname, path, allPaths);
  const isNewsActive = isActive('/news');
  const isInsightsActive = isActive('/insights');

  return (
    <nav
      ref={navRef}
      className={cn(
        'h-12 px-4 border-b border-border bg-card w-full flex items-center justify-between shrink-0',
        className,
      )}
    >
      <div className="flex items-center gap-0.5">
        {groups.map((group) => (
          <TopNavGroupView
            key={group.label}
            group={group}
            align="left"
            isActive={isActive}
            isOpen={openGroup === group.label}
            registerTrigger={registerTrigger}
            openNow={openNow}
            scheduleClose={scheduleClose}
            closeNow={closeNow}
            handleGroupFocus={handleGroupFocus}
          />
        ))}
        {/* TRAD-3867(10/30 davidyoon) — «인사이트»: 금융 그룹 바로 다음. 랜딩 하나뿐인 섹션이라 드롭다운 없이 뉴스와 같은 낱개 링크 규격 —
            금융 지표·캘린더(데이터) 바로 옆에 «그걸 읽는 글»을 둔다. */}
        <Link
          to="/insights"
          className={cn(
            'flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors',
            isInsightsActive ? 'text-brand-text' : 'text-muted-foreground hover:text-foreground hover:bg-secondary',
          )}
        >
          {t('nav.insights')}
        </Link>
        <Link
          to="/news"
          className={cn(
            'flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors',
            isNewsActive ? 'text-brand-text' : 'text-muted-foreground hover:text-foreground hover:bg-secondary',
          )}
        >
          {t('nav.news.label')}
        </Link>
        <TopNavGroupView
          group={communityGroup}
          align="left"
          isActive={isActive}
          isOpen={openGroup === communityGroup.label}
          registerTrigger={registerTrigger}
          openNow={openNow}
          scheduleClose={scheduleClose}
          closeNow={closeNow}
          handleGroupFocus={handleGroupFocus}
        />
      </div>

      <div className="flex items-center">
        <TopNavGroupView
          group={myInfoGroup}
          align="right"
          isActive={isActive}
          isOpen={openGroup === myInfoGroup.label}
          registerTrigger={registerTrigger}
          openNow={openNow}
          scheduleClose={scheduleClose}
          closeNow={closeNow}
          handleGroupFocus={handleGroupFocus}
        />
      </div>
    </nav>
  );
};
